import React, { useState, useMemo } from "react";
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Box,
  Chip,
  Button,
  Pagination,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Circle as UnreadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useNotifications,
  useMarkNotificationsRead,
  useMarkAllNotificationsRead,
  type Notification,
} from "../../hooks/useApi";
import { useNotification } from "../../contexts/NotificationContext";

const ITEMS_PER_PAGE = 20;

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const queryParams = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      unread_only: filter === "unread",
    }),
    [page, filter]
  );

  const { data, isLoading, error } = useNotifications(queryParams);
  const markReadMutation = useMarkNotificationsRead();
  const markAllReadMutation = useMarkAllNotificationsRead();

  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.is_read) return;

    try {
      await markReadMutation.mutateAsync({
        notification_ids: [notification.id],
      });
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
      showError("通知を既読にできませんでした");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllReadMutation.mutateAsync();
      showSuccess("すべての通知を既読にしました");
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
      showError("通知を既読にできませんでした");
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      handleMarkAsRead(notification);
    }

    // Navigate based on notification type
    if (notification.related_inquiry_id) {
      navigate(`/inquiries/${notification.related_inquiry_id}`);
    } else if (notification.related_item_id) {
      navigate(`/items/${notification.related_item_id}`);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "inquiry_new":
      case "inquiry_reply":
        return <InfoIcon color="info" />;
      case "item_found":
        return <CheckCircleIcon color="success" />;
      case "item_claimed":
      case "item_returned":
        return <CheckIcon color="primary" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getTitle = (notification: Notification) => {
    return notification.title;
  };

  const totalPages = data ? Math.ceil(data.total_count / ITEMS_PER_PAGE) : 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          通知
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DoneAllIcon />}
          onClick={handleMarkAllAsRead}
          disabled={
            !data || data.unread_count === 0 || markAllReadMutation.isPending
          }
        >
          すべて既読にする
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={filter}
          onChange={(_, newValue) => {
            setFilter(newValue);
            setPage(1);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="すべて" value="all" />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                未読
                {data && data.unread_count > 0 && (
                  <Chip
                    label={data.unread_count}
                    size="small"
                    color="error"
                    sx={{ height: 20, minWidth: 20, px: 0.5 }}
                  />
                )}
              </Box>
            }
            value="unread"
          />
        </Tabs>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">
          通知の取得に失敗しました: {error.message}
        </Alert>
      ) : data?.notifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">通知はありません</Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {data?.notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  disablePadding
                  secondaryAction={
                    !notification.is_read && (
                      <IconButton
                        edge="end"
                        aria-label="mark as read"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification);
                        }}
                        size="small"
                      >
                        <UnreadIcon color="primary" sx={{ fontSize: 12 }} />
                      </IconButton>
                    )
                  }
                >
                  <ListItemButton
                    alignItems="flex-start"
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      bgcolor: notification.is_read
                        ? "transparent"
                        : "action.hover",
                      "&:hover": {
                        bgcolor: notification.is_read
                          ? "action.hover"
                          : "action.selected",
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "background.paper" }}>
                        {getIcon(notification.type_)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pr: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            component="span"
                            sx={{
                              fontWeight: notification.is_read
                                ? "normal"
                                : "bold",
                            }}
                          >
                            {getTitle(notification)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: "nowrap", ml: 2 }}
                          >
                            {new Date(notification.created_at).toLocaleString(
                              "ja-JP"
                            )}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mt: 0.5,
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
              />
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default NotificationsPage;
